import '../styles/Home.css';
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { RiArrowRightCircleFill, RiPlayCircleFill } from 'react-icons/ri';
import { useApiCall } from '../hooks/useApiCall';

export default function Home() {
  const navigate = useNavigate();
  const apiCall = useApiCall();

  const [banners, setBanners] = useState([]);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [metrics, setMetrics] = useState({ new_arrivals_count: 0, new_arrivals_preview: [] });

  // Fetch banners
  useEffect(() => {
    const fetchBanners = async () => {
      setBannersLoading(true);
      try {
        const response = await apiCall('home/banners', 'GET');
        if (response.success && response.data.banners) {
          setBanners(response.data.banners);
        }
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setBannersLoading(false);
      }
    };
    fetchBanners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch home metrics separately (new arrivals count + last 3 preview items)
  useEffect(() => {
    apiCall('home/metrics', 'POST', {}).then((response) => {
      if (response?.success) {
        setMetrics(response.data);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Create URL-friendly slug from category name
  const createSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s&]/g, '')
      .replace(/\s*&\s*/g, '-and-')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleBannerClick = (banner) => {
    // Check if this is a "New Arrivals" banner (category_id = 0 or is_new_arrivals flag)
    if (banner.is_new_arrivals || banner.category_id === 0) {
      navigate('/products?sort=new');
    } else if (banner.category_id && banner.category_name) {
      const slug = createSlug(banner.category_name);
      navigate(`/products/category/${banner.category_id}/${slug}`);
    } else {
      navigate('/products');
    }
  };

  // Split banners into two groups - insert New Arrivals after 3rd banner
  const firstBanners = banners.slice(0, 3);
  const remainingBanners = banners.slice(3);

  return (
    <div className="home-page">
      
      {bannersLoading ? (
        <div className="home-loading">
          <p>Loading...</p>
        </div>
      ) : (
        <>
          {/* First 3 banners */}
          {firstBanners.map((banner) => {
            const title = banner.title || banner.category_name || 'Explore Our Products';
            const subtitle = banner.subtitle || '';
            const cta = `Explore ${banner.category_name || 'Products'}`;
            
            return (
              <div 
                key={banner.id} 
                className="home-banner"
                style={{ 
                  backgroundImage: banner.image ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${banner.image})` : '',
                }}
                onClick={() => handleBannerClick(banner)}
              >
                <div className="home-banner-content">
                  <h2 className="home-banner-title">{title}</h2>
                  {subtitle && <p className="home-banner-subtitle">{subtitle}</p>}
                  <button className="home-banner-cta">
                    {cta}
                    <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* New Arrivals Banner - only visible when new_arrival products exist */}
          {metrics.new_arrivals_count > 0 && (
            <div
              className="home-banner home-banner-new-arrivals"
              onClick={() => navigate('/products?sort=new')}
            >
              <div className="home-na-left">
                <span className="home-banner-badge">NEW</span>
                <h2 className="home-banner-title">New Arrivals</h2>
                <p className="home-banner-subtitle">
                  {metrics.new_arrivals_count} new product{metrics.new_arrivals_count !== 1 ? 's' : ''} just landed
                </p>
                <button className="home-banner-cta">
                  Shop New Arrivals
                  <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                </button>
              </div>
              <div className="home-na-products">
                {metrics.new_arrivals_preview.map((product) => (
                  <div
                    key={product.id}
                    className="home-na-card"
                    onClick={(e) => {
                      e.stopPropagation();
                      const slug = product.name.toLowerCase().replace(/[^a-z0-9\s&]/g,'').replace(/\s*&\s*/g,'-and-').replace(/\s+/g,'-').replace(/-+/g,'-').replace(/^-|-$/g,'');
                      navigate(`/products/product/${product.id}/${slug}`);
                    }}
                  >
                    {product.image
                      ? <img src={product.image} alt={product.name} className="home-na-card-image" />
                      : <div className="home-na-card-image home-na-card-no-image" />}
                    <div className="home-na-card-info">
                      <div className="home-na-card-name">{product.name}</div>
                      <div className="home-na-card-price">
                        {product.coming_soon
                          ? <span className="home-na-coming-soon">Coming Soon</span>
                          : `${product.price.curr} ${product.price.amount}`
                        }
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Remaining banners */}
          {remainingBanners.map((banner) => {
            const title = banner.title || banner.category_name || 'Explore Our Products';
            const subtitle = banner.subtitle || '';
            const cta = `Explore ${banner.category_name || 'Products'}`;
            
            return (
              <div 
                key={banner.id} 
                className="home-banner"
                style={{ 
                  backgroundImage: banner.image ? `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url(${banner.image})` : '',
                }}
                onClick={() => handleBannerClick(banner)}
              >
                <div className="home-banner-content">
                  <h2 className="home-banner-title">{title}</h2>
                  {subtitle && <p className="home-banner-subtitle">{subtitle}</p>}
                  <button className="home-banner-cta">
                    {cta}
                    <RiArrowRightCircleFill className="home-banner-arrow" size={24} />
                  </button>
                </div>
              </div>
            );
          })}

          {banners.length === 0 && (
            <div className="home-no-banners">
              <p>No banners available at the moment.</p>
            </div>
          )}

          {/* Virtual Showroom Banner */}
          <div className="home-showroom-banner" onClick={() => navigate('/experience')}>
            <div className="home-showroom-bg" />
            <div className="home-showroom-content">
              <span className="home-showroom-badge">IMMERSIVE EXPERIENCE</span>
              <h2 className="home-showroom-title">Virtual Showroom</h2>
              <p className="home-showroom-subtitle">Step inside our curated collection. Explore room setups, discover products and shop directly from the scene.</p>
              <button className="home-showroom-cta">
                <RiPlayCircleFill size={22} />
                Enter Showroom
              </button>
            </div>
            <div className="home-showroom-decoration">
              <div className="home-showroom-ring home-showroom-ring-1" />
              <div className="home-showroom-ring home-showroom-ring-2" />
              <div className="home-showroom-ring home-showroom-ring-3" />
            </div>
          </div>
        </>
      )}

    </div>
  );
}
